import { useState, useEffect } from 'react'
import { Save, CheckCircle, ExternalLink, Settings2, Bot, FileCode } from 'lucide-react'
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui'
import { api } from '@/lib/api/client'

interface Settings {
  site_url: string
  wp_user: string
  wp_app_password: string
  serpapi_key: string
  anthropic_key: string
  notification_email: string
  claude_model: string
  prompt_guida: string
  prompt_articolo: string
  prompt_landing: string
  html_template_guida: string
  html_template_articolo: string
  html_template_landing: string
  custom_css_url: string
}

const CLAUDE_MODELS = [
  { id: 'claude-sonnet-4-5-20250929', name: 'Claude Sonnet 4.5 (Raccomandato)', description: 'Miglior rapporto qualità/prezzo' },
  { id: 'claude-sonnet-4-20250514', name: 'Claude Sonnet 4', description: 'Veloce e affidabile' },
  { id: 'claude-opus-4-5-20251101', name: 'Claude Opus 4.5', description: 'Massima qualità, più costoso' },
]

const DEFAULT_PROMPTS = {
  guida: `Sei un esperto SEO e content strategist italiano specializzato in guide complete.

OBIETTIVO: Migliorare la guida per posizionarsi meglio su Google e convertire lettori.

ELEMENTI STRUTTURALI DA VERIFICARE:
- intro_box: Box introduttivo con sintesi (OBBLIGATORIO)
- toc: Indice dei contenuti con anchor links
- faq: Sezione FAQ con risposte alle PAA
- cta_box: Call-to-action per conversione
- sources: Footer con fonti autorevoli
- tables: Tabelle comparative dove utili
- internal_links: Minimo 3 link interni pertinenti

STILE:
- Tono professionale ma accessibile
- Frasi brevi, paragrafi corti
- Keyword naturalmente integrate
- H2/H3 che rispondono a search intent`,

  articolo: `Sei un esperto SEO italiano specializzato in articoli blog informativi.

OBIETTIVO: Creare contenuto engaging che risponda alle domande degli utenti.

ELEMENTI STRUTTURALI:
- Intro coinvolgente nei primi 100 caratteri
- Sottotitoli H2 ogni 300 parole circa
- Almeno 1 lista puntata o numerata
- CTA finale verso servizi correlati
- Link interni a guide approfondimento

STILE:
- Tono conversazionale
- Esempi pratici e concreti
- Evita gergo tecnico non necessario`,

  landing: `Sei un esperto copywriter specializzato in landing page per conversione.

OBIETTIVO: Massimizzare conversioni mantenendo valore SEO.

ELEMENTI STRUTTURALI:
- Hero section con value proposition chiara
- Social proof (testimonial, numeri, loghi)
- Benefici > Caratteristiche
- FAQ per obiezioni comuni
- CTA multipli (above fold, mid-page, footer)
- Form contatto semplificato

STILE:
- Copy diretto e persuasivo
- Urgenza senza essere aggressivo
- Focus su benefici per l'utente`,
}

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<'general' | 'ai' | 'prompts'>('general')
  const [settings, setSettings] = useState<Settings>({
    site_url: '',
    wp_user: '',
    wp_app_password: '',
    serpapi_key: '',
    anthropic_key: '',
    notification_email: '',
    claude_model: 'claude-sonnet-4-5-20250929',
    prompt_guida: DEFAULT_PROMPTS.guida,
    prompt_articolo: DEFAULT_PROMPTS.articolo,
    prompt_landing: DEFAULT_PROMPTS.landing,
    html_template_guida: '',
    html_template_articolo: '',
    html_template_landing: '',
    custom_css_url: '',
  })
  const [isSaving, setIsSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [testResults, setTestResults] = useState<{
    wordpress?: 'success' | 'error' | null
    serpapi?: 'success' | 'error' | null
    anthropic?: 'success' | 'error' | null
  }>({})

  useEffect(() => {
    const loadSettings = async () => {
      const result = await api.get<{ settings: Settings }>('/settings')
      if (result.success && result.data) {
        setSettings({
          ...settings,
          ...result.data.settings,
          // Use defaults if not set
          prompt_guida: result.data.settings.prompt_guida || DEFAULT_PROMPTS.guida,
          prompt_articolo: result.data.settings.prompt_articolo || DEFAULT_PROMPTS.articolo,
          prompt_landing: result.data.settings.prompt_landing || DEFAULT_PROMPTS.landing,
        })
      }
    }
    loadSettings()
  }, [])

  const handleChange = (field: keyof Settings, value: string) => {
    setSettings((prev) => ({ ...prev, [field]: value }))
    setSaved(false)
  }

  const handleSave = async () => {
    setIsSaving(true)
    const result = await api.put('/settings', settings)
    if (result.success) {
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    }
    setIsSaving(false)
  }

  const testConnection = async (service: 'wordpress' | 'serpapi' | 'anthropic') => {
    setTestResults((prev) => ({ ...prev, [service]: null }))
    const result = await api.post(`/settings/test/${service}`)
    setTestResults((prev) => ({
      ...prev,
      [service]: result.success ? 'success' : 'error',
    }))
  }

  const resetPrompt = (type: 'guida' | 'articolo' | 'landing') => {
    handleChange(`prompt_${type}` as keyof Settings, DEFAULT_PROMPTS[type])
  }

  const tabs = [
    { id: 'general' as const, label: 'Generale', icon: Settings2 },
    { id: 'ai' as const, label: 'AI & Modelli', icon: Bot },
    { id: 'prompts' as const, label: 'Prompt Templates', icon: FileCode },
  ]

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Impostazioni</h1>
        <p className="text-sm text-gray-500 mt-1">
          Configura credenziali, modelli AI e prompt templates
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="space-y-6 max-w-3xl">
        {/* General Tab */}
        {activeTab === 'general' && (
          <>
            {/* WordPress */}
            <Card>
              <CardHeader>
                <CardTitle>WordPress</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  label="URL sito"
                  value={settings.site_url}
                  onChange={(e) => handleChange('site_url', e.target.value)}
                  placeholder="https://dokicasa.it"
                />
                <Input
                  label="Username"
                  value={settings.wp_user}
                  onChange={(e) => handleChange('wp_user', e.target.value)}
                  placeholder="admin"
                />
                <Input
                  label="Application Password"
                  type="password"
                  value={settings.wp_app_password}
                  onChange={(e) => handleChange('wp_app_password', e.target.value)}
                  placeholder="xxxx xxxx xxxx xxxx"
                />
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => testConnection('wordpress')}
                  >
                    Test connessione
                  </Button>
                  {testResults.wordpress === 'success' && (
                    <span className="text-green-600 text-sm flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Connesso
                    </span>
                  )}
                  {testResults.wordpress === 'error' && (
                    <span className="text-red-600 text-sm">Errore connessione</span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* API Keys */}
            <Card>
              <CardHeader>
                <CardTitle>API Keys</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Input
                    label="SerpAPI Key"
                    type="password"
                    value={settings.serpapi_key}
                    onChange={(e) => handleChange('serpapi_key', e.target.value)}
                    placeholder="xxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                  <a
                    href="https://serpapi.com/dashboard"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary-600 hover:underline mt-1 inline-flex items-center gap-1"
                  >
                    Ottieni API key <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div>
                  <Input
                    label="Anthropic API Key"
                    type="password"
                    value={settings.anthropic_key}
                    onChange={(e) => handleChange('anthropic_key', e.target.value)}
                    placeholder="sk-ant-..."
                  />
                  <a
                    href="https://console.anthropic.com/settings/keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary-600 hover:underline mt-1 inline-flex items-center gap-1"
                  >
                    Ottieni API key <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Notifications */}
            <Card>
              <CardHeader>
                <CardTitle>Notifiche</CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  label="Email per digest"
                  type="email"
                  value={settings.notification_email}
                  onChange={(e) => handleChange('notification_email', e.target.value)}
                  placeholder="tu@esempio.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Riceverai un riepilogo settimanale delle proposte in attesa
                </p>
              </CardContent>
            </Card>
          </>
        )}

        {/* AI Tab */}
        {activeTab === 'ai' && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Modello Claude</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {CLAUDE_MODELS.map((model) => (
                    <label
                      key={model.id}
                      className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                        settings.claude_model === model.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="claude_model"
                        value={model.id}
                        checked={settings.claude_model === model.id}
                        onChange={(e) => handleChange('claude_model', e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium">{model.name}</div>
                        <div className="text-sm text-gray-500">{model.description}</div>
                        <div className="text-xs text-gray-400 mt-1 font-mono">{model.id}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>CSS Custom da WordPress</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  URL del file CSS custom su WordPress. Verrà caricato automaticamente durante la generazione per garantire stili aggiornati.
                </p>
                <Input
                  label="URL CSS Custom"
                  value={settings.custom_css_url}
                  onChange={(e) => handleChange('custom_css_url', e.target.value)}
                  placeholder="https://dokicasa.it/wp-content/themes/theme/custom.css"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Lascia vuoto se non usi CSS custom. L'AI userà le classi con prefisso "wpa-".
                </p>
              </CardContent>
            </Card>
          </>
        )}

        {/* Prompts Tab */}
        {activeTab === 'prompts' && (
          <>
            {(['guida', 'articolo', 'landing'] as const).map((type) => (
              <Card key={type}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="capitalize">Tipo: {type}</CardTitle>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => resetPrompt(type)}
                    >
                      Reset prompt default
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Prompt */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prompt AI
                    </label>
                    <textarea
                      className="w-full h-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={settings[`prompt_${type}` as keyof Settings] as string}
                      onChange={(e) => handleChange(`prompt_${type}` as keyof Settings, e.target.value)}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Istruzioni per l'AI su come analizzare e migliorare articoli di tipo "{type}".
                    </p>
                  </div>

                  {/* HTML Template */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Template HTML
                    </label>
                    <textarea
                      className="w-full h-48 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={settings[`html_template_${type}` as keyof Settings] as string}
                      onChange={(e) => handleChange(`html_template_${type}` as keyof Settings, e.target.value)}
                      placeholder={`<!-- Esempio struttura per ${type} -->
<div class="wpa-intro-box">
  <p><strong>In breve:</strong> Sintesi...</p>
</div>

<h2>Sezione principale</h2>
<p>Contenuto...</p>

<div class="wpa-cta-box">
  <h3>CTA</h3>
  <a href="/contatti">Contattaci</a>
</div>`}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Esempio di HTML da usare come riferimento per struttura e stile.
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        )}

        {/* Save */}
        <div className="flex items-center gap-4 pt-4 border-t">
          <Button onClick={handleSave} isLoading={isSaving}>
            <Save className="w-4 h-4 mr-2" />
            Salva impostazioni
          </Button>
          {saved && (
            <span className="text-green-600 text-sm flex items-center gap-1">
              <CheckCircle className="w-4 h-4" /> Salvato
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
